import 'react';
import {Box, Table, TableBody, TableCell, TableRow} from '@mui/material';
import type {ProfitsTaxAVO} from '@/services/auditService/interface/tax-com';
import {MoneyField} from '@/components/common';
import {amountCellSx, descCellSx, labelCell, labelCellSx} from '@/pages/components/tax-computation/style/tax-style';
import {TaxMainP1Footnotes} from '@/pages/components/tax-computation/main/TaxMainP1Footnotes';
import {DEFAULT_P1_NOTE} from '@/pages/components/tax-computation/taxComP1Text';
import {HKD_SYMBOL, isHkdCurrency} from '@/pages/components/tax-computation/taxCurrency';

interface TaxMainTypeAProps {
    data?: ProfitsTaxAVO | null;
    lessTotal?: number | null;
    currency?: string | null;
    taxLossBf?: number;
    taxLossBfChange?: (taxLossBf: number) => void;
    note?: string | null;
    confirm?: string | null;
    noteChange?: (note: string) => void;
    confirmChange?: (confirm: string) => void;
    onTextBlur?: () => void;
}

export function TaxMainTypeA({
                                 data,
                                 lessTotal,
                                 currency,
                                 taxLossBf,
                                 taxLossBfChange,
                                 note,
                                 confirm,
                                 noteChange,
                                 confirmChange,
                                 onTextBlur,
                             }: TaxMainTypeAProps) {
    const renderAmount = (value?: number | null, currencySymbol = currency || '') => {
        if (value === undefined || value === null) return null;
        return (
            <MoneyField
                value={value}
                editable={false}
                currencySymbol={currencySymbol}
                showCents={false}
                align="right"
            />
        );
    };

    const useHkdForExchange = !isHkdCurrency(currency);

    const shouldHideRow = (value?: number | null) => value === 0;
    const addValue = lessTotal === undefined || lessTotal === null ? null : -lessTotal;
    const taxLossBfValue = taxLossBf ?? data?.taxLossBf ?? 0;
    const showAdjustedLoss = (data?.adjustedAssessment ?? 0) !== 0;
    const showTaxLossCf = (data?.taxLossCf ?? 0) !== 0;
    const showExchangeRow = (data?.afterRateValue ?? 0) !== 0 && (currency || '').toUpperCase() !== 'HKD';

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
            <Table size="small">
                <TableBody>
                    <TableRow>
                        <TableCell colSpan={3} sx={{py: 0.25, verticalAlign: 'middle'}}/>
                    </TableRow>

                    {/*{shouldHideRow(addValue) ? null : (*/}
                    <TableRow>
                        <TableCell sx={labelCell}>ADD:</TableCell>
                        <TableCell sx={descCellSx}>{data?.addName || ''}</TableCell>
                        <TableCell sx={amountCellSx} align="right">{renderAmount(addValue)}</TableCell>
                    </TableRow>
                    {/*)}*/}

                    <TableRow>
                        <TableCell colSpan={3} sx={descCellSx}/>
                    </TableRow>

                    {/*{shouldHideRow(data?.assessmentValue) ? null : (*/}
                    <TableRow>
                        <TableCell sx={labelCell}>ASSESSABLE PROFIT:</TableCell>
                        <TableCell sx={descCellSx}>{data?.assessmentName || ''}</TableCell>
                        <TableCell sx={amountCellSx} align="right">{renderAmount(data?.assessmentValue)}</TableCell>
                    </TableRow>
                    {/*)}*/}

                    <TableRow>
                        <TableCell colSpan={3} sx={descCellSx}/>
                    </TableRow>

                    <TableRow>
                        <TableCell sx={labelCellSx}>Final assessment：-</TableCell>
                        <TableCell colSpan={2} sx={descCellSx}/>
                    </TableRow>

                    <TableRow>
                        <TableCell sx={labelCellSx}>Tax loss b/f</TableCell>
                        <TableCell sx={descCellSx}/>
                        <TableCell sx={{...amountCellSx, paddingRight: 0.5}} align="right">
                            <MoneyField
                                value={taxLossBfValue}
                                editable={!!taxLossBfChange}
                                tiny
                                currencySymbol={currency || ''}
                                showCents={false}
                                align="right"
                                onChangeNumber={(n) => {
                                    const next = n > 0 ? -Math.abs(n) : n;
                                    taxLossBfChange?.(next);
                                }}
                            />
                        </TableCell>
                    </TableRow>

                    {showAdjustedLoss ? (
                        <TableRow>
                            <TableCell colSpan={2} sx={labelCellSx}>{data?.adjustedAssessmentName || ''}</TableCell>
                            <TableCell sx={amountCellSx} align="right">{renderAmount(data?.adjustedAssessment)}</TableCell>
                        </TableRow>
                    ) : null}

                    {showTaxLossCf ? (
                        <TableRow>
                            <TableCell sx={labelCellSx}>Tax loss c/f</TableCell>
                            <TableCell sx={descCellSx}></TableCell>
                            <TableCell sx={amountCellSx} align="right">{renderAmount(data?.taxLossCf)}</TableCell>
                        </TableRow>
                    ) : null}

                    {showExchangeRow ? (
                        <TableRow>
                            <TableCell sx={labelCellSx}></TableCell>
                            <TableCell sx={descCellSx}>{data?.exchangeColumn || ''}</TableCell>
                            <TableCell sx={amountCellSx} align="right">
                                {renderAmount(data?.afterRateValue, useHkdForExchange ? HKD_SYMBOL : (currency || ''))}
                            </TableCell>
                        </TableRow>
                    ) : null}
                </TableBody>
            </Table>

            <TaxMainP1Footnotes
                note={note}
                confirm={confirm}
                fallbackNote={data?.note}
                fallbackConfirm={data?.confirm}
                defaultNote={DEFAULT_P1_NOTE}
                noteChange={noteChange}
                confirmChange={confirmChange}
                onTextBlur={onTextBlur}
            />
        </Box>    );
}
